import { useState, useCallback } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import {
  Box,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  Link,
  CircularProgress,
} from "@mui/material";
import { useAuth } from "../../contexts/auth";
import { colors } from "../../pages";
import AuthPage, {
  authTextFieldSx,
  authSubmitButtonSx,
} from "../auth-page/auth-page";

export default function LoginForm() {
  const navigate = useNavigate();
  const { signIn } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    rememberMe: false,
  });
  const [errors, setErrors] = useState({});
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "error",
    duration: 2000,
  });

  const showNotification = useCallback(
    (message, severity = "error", duration = 2000) => {
      setNotification({ open: true, message, severity, duration });
    },
    []
  );

  const validateForm = useCallback(() => {
    const newErrors = {};

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Email is invalid";
    }

    if (!formData.password) {
      newErrors.password = "Password is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  const handleInputChange = (field) => (event) => {
    const value =
      field === "rememberMe" ? event.target.checked : event.target.value;

    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const onSubmit = useCallback(
    async (e) => {
      e.preventDefault();

      if (!validateForm()) {
        return;
      }

      const { email, password } = formData;
      setLoading(true);

      const result = await signIn(email, password);

      if (result.updatePassword) {
        navigate("/change-password", {
          state: { email },
        });
        return;
      }

      if (result.resetPassword) {
        navigate("/reset-password");
        return;
      }

      if (result.unconfirmed) {
        console.log(result.error);
        setLoading(false);
        showNotification(result.message, "error", 5000);
        return;
      }

      if (!result.isOk) {
        setLoading(false);
        showNotification(result.message, "error", 2000);
        return;
      }

      navigate("/");
    },
    [signIn, formData, validateForm, navigate, showNotification]
  );

  return (
    <AuthPage
      subtitle="Please sign in before continuing."
      notification={notification}
      onCloseNotification={() =>
        setNotification((prev) => ({ ...prev, open: false }))
      }
    >
        <Box
          component="form"
          onSubmit={onSubmit}
          noValidate
          sx={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            gap: 1.75,
          }}
        >
          <TextField
            name="email"
            type="email"
            placeholder="Email"
            variant="outlined"
            value={formData.email}
            onChange={handleInputChange("email")}
            error={!!errors.email}
            helperText={errors.email}
            disabled={loading}
            fullWidth
            autoComplete="email"
            InputLabelProps={{ shrink: false }}
            sx={authTextFieldSx}
          />

          <TextField
            name="password"
            type="password"
            placeholder="Password"
            variant="outlined"
            value={formData.password}
            onChange={handleInputChange("password")}
            error={!!errors.password}
            helperText={errors.password}
            disabled={loading}
            fullWidth
            autoComplete="current-password"
            InputLabelProps={{ shrink: false }}
            sx={authTextFieldSx}
          />

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mt: -0.5,
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.rememberMe}
                  onChange={handleInputChange("rememberMe")}
                  disabled={loading}
                  name="rememberMe"
                  size="small"
                  sx={{
                    color: colors.muted,
                    py: 0.5,
                    "&.Mui-checked": {
                      color: colors.gold,
                    },
                    "&.Mui-disabled": {
                      color: `${colors.muted} !important`,
                    },
                    "&.Mui-disabled.Mui-checked": {
                      color: `${colors.gold} !important`,
                    },
                  }}
                />
              }
              label="Remember me"
              sx={{
                m: 0,
                "& .MuiFormControlLabel-label": {
                  color: colors.muted,
                  fontSize: "0.8rem",
                },
                "& .MuiFormControlLabel-label.Mui-disabled": {
                  color: `${colors.muted} !important`,
                },
              }}
            />

            <Link
              component={RouterLink}
              to="/reset-password"
              underline="hover"
              color="inherit"
              sx={{
                color: `${colors.muted} !important`,
                fontSize: "0.8rem",
                "&:hover": {
                  color: `${colors.muted} !important`,
                },
              }}
            >
              Forgot Password?
            </Link>
          </Box>

          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            fullWidth
            sx={authSubmitButtonSx}
          >
            {loading ? (
              <CircularProgress size={24} sx={{ color: colors.black }} />
            ) : (
              "Sign In to QUE"
            )}
          </Button>
        </Box>
    </AuthPage>
  );
}
